<template>
  <div>
    <content-loading v-if="isInitializing" />
    <div v-else>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="d-flex" style="margin-bottom: 1.70rem;" @click="goBack()">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="38" viewBox="0 0 40 38" fill="none" style="cursor: pointer;">
          <path
            d="M38.75 19.0003H1.25M1.25 19.0003L18.9583 1.29199M1.25 19.0003L18.9583 36.7087"
            stroke="#FF7C48"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <div class="d-flex align-items-center" style="margin-left: 1.25rem; gap: 2.34rem;">
          <div>
            <h2 class="mb-1">{{ $t("audit.policy-management") }}</h2>
            <h5 v-if="firm" class="text-muted mb-0">{{ firm.name | translate }}</h5>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center">
        <b-dropdown split split-href="#" :text="$t('report-template.type.findeks')" size="sm" variant="light" class="mr-2 ">
          <b-dropdown-item @click="prepareReport(firm)" href="#">{{ $t("common.download") }}</b-dropdown-item>
        </b-dropdown>

        <b-button variant="light" size="sm" class="mr-2" @click="refresh">
          <i class="bx bx-refresh"></i>
          {{ $t("common.refresh") }}
        </b-button>
      </div>
    </div>

    <div class="d-flex mt-4 align-items-center justify-content-center">
      <div class="search2 ">
        <div class="search2--icon"><i class="iconoir-search"></i></div>
        <input v-model="nameFilter" class="search2--input" type="text" :placeholder="$t('common.search')" />
      </div>
      <div class="ml-4">
        <select class="form-control" v-model="questionCategoryFilter">
          <option :value="undefined">{{ $t("common.clear-selection") }}</option>
          <option value="TEST">{{ $t("firm.status.test") }}</option>
          <option value="PROD">{{ $t("firm.status.prod") }}</option>
          <option value="POST_PROD">{{ $t("firm.status.post-prod") }}</option>
        </select>
      </div>
    </div>

    <div v-if="firm" class="mb-4 mt-4">
      <span class="mr-4 policy-card__icon">
        <i class="mdi mdi-help text-main"></i>

        <strong>{{ $t("firm.support-service") }}:</strong>
        {{ firm.supportService ? $t("common.yes") : $t("common.no") }}
      </span>
      <span class="mr-4 policy-card__icon">
        <i class="mdi mdi-server text-main"></i> <strong>{{ $t("firm.virtual-server") }}:</strong>
        {{ firm.virtualServer ? $t("common.yes") : $t("common.no") }}
      </span>
      <span class="mr-4 policy-card__icon" v-if="firmStatus">
        <i class="mdi mdi-office-building text-main"></i>
        <strong>{{ $t("firm.status.text") }}:</strong>
        <firm-status :status="firmStatus" />
      </span>
      <span class="mr-4 policy-card__icon">
        <i class="mdi mdi-shield-check text-main"></i>
        <strong>{{ $t("audit.completed-policy-count") }}:</strong>
        {{ completedPolicyCount }}
      </span>
    </div>

    <b-tabs v-model="activeTabIndex" lazy>
      <b-tab v-for="(tab, index) in statusTabs" :key="tab.key" :title="$t(tab.labelKey)">
        <div v-if="isTabActive(tab.key)">
          <content-loading v-if="isDetailLoading" />
          <template v-else>
            <div v-if="hasDocs" class="table-responsive">
              <table class="table project-list-table compact ">
                <thead>
                  <tr>
                    <th>{{ $t("audit.table.control-name") }}</th>
                    <th>{{ $t("audit.table.control-description") }}</th>
                    <th>{{ $t("audit.table.control-category") }}</th>
                    <th>{{ $t("audit.table.control-status") }}</th>
                    <th>{{ $t("audit.table.related-questions") }}</th>
                    <th>{{ $t("audit.table.question-status") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="block in policyBlocks">
                    <template v-if="block.questionCount">
                      <tr
                        v-for="(question, questionIndex) in block.questions"
                        :key="`${block.key}-question-${question._id || question.questionId || questionIndex}`"
                        :class="['policy-table__row', { 'policy-table__row--first': questionIndex === 0 }]"
                      >
                        <td v-if="questionIndex === 0" :rowspan="block.questionCount" class="policy-table__cell policy-table__cell--name">
                          {{ block.policy.name | translate }}
                        </td>
                        <td v-if="questionIndex === 0" :rowspan="block.questionCount" class="policy-table__cell">
                          <span v-if="block.policy.description">{{ block.policy.description | translate }}</span>
                          <span v-else>-</span>
                        </td>
                        <td v-if="questionIndex === 0" :rowspan="block.questionCount" class="policy-table__cell">
                          <span v-if="block.policy.category">{{ block.policy.category.name | translate }}</span>
                          <span v-else>-</span>
                        </td>
                        <td v-if="questionIndex === 0" :rowspan="block.questionCount" class="policy-table__cell policy-table__cell--status">
                          <span class="badge badge-soft-primary policy-table__status">{{ formatPolicyStatus(block.policy.status) }}</span>
                        </td>
                        <td class="policy-table__cell policy-table__cell--question word-break">
                          <span class="policy-table__question-bullet">•</span>
                          <span class="policy-table__question-text">{{ formatQuestionLabel(question) }}</span>
                        </td>
                        <td class="policy-table__cell policy-table__cell--question-status text-center">
                          <span class="badge badge-light policy-table__question-status">{{ formatQuestionStatus(question.reviewStatus) }}</span>
                        </td>
                      </tr>
                    </template>
                    <tr v-else :key="`${block.key}-no-question`" class="policy-table__row policy-table__row--single">
                      <td class="policy-table__cell policy-table__cell--name">{{ block.policy.name | translate }}</td>
                      <td class="policy-table__cell">
                        <span v-if="block.policy.description">{{ block.policy.description | translate }}</span>
                        <span v-else>-</span>
                      </td>
                      <td class="policy-table__cell">
                        <span v-if="block.policy.category">{{ block.policy.category.name | translate }}</span>
                        <span v-else>-</span>
                      </td>
                      <td class="policy-table__cell policy-table__cell--status">
                        <span class="badge badge-soft-primary policy-table__status">{{ formatPolicyStatus(block.policy.status) }}</span>
                      </td>
                      <td class="policy-table__cell policy-table__cell--question">-</td>
                      <td class="policy-table__cell policy-table__cell--question-status">-</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <empty v-else>
              <h6 class="text-center text-muted mb-0">{{ $t("common.empty") }}</h6>
            </empty>

            <div v-if="pagination" class="row mb-5 pb-5">
              <div class="col">
                <ul class="pagination pagination-rounded mb-0">
                  <b-pagination v-model="request.options.page" :total-rows="pagination.totalDocs" :per-page="pagination.limit" limit="10" size="sm"></b-pagination>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </b-tab>
    </b-tabs>
    </div>
  </div>
</template>

<script>
import { getFirmStatus } from "@/helpers/filters";
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";
import FirmStatus from "@cc/firm-status";

export default {
  components: { ContentLoading, Empty, FirmStatus },
  props: {
    firmId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      nameFilter: undefined,
      questionCategoryFilter: undefined,
      statusTabs: [
        { key: "IN_PROGRESS", labelKey: "audit.policy-groups.audit-process" },
        { key: "COMPLETED", labelKey: "audit.policy-groups.approval-completed" },
      ],
      activeTabIndex: 0,
      request: {
        options: { page: 1, limit: 15, sort: { createdAt: "desc" } },
        query: { status: { value: "IN_PROGRESS", isObject: true } },
      },
      isInitializing: true, // Loading state for deep link hydration
    };
  },
  async mounted() {
    // Deep link support: fetch firm and audit if not in store
    try {
      // 1. Fetch firm if not loaded OR if loaded firm ID differs from URL param
      if (this.firmId && (!this.firm || this.firm._id !== this.firmId)) {
        await this.$store.dispatch("organizationFirm/findById", this.firmId);
      }

      // 2. Fetch audit if provided in route and (not loaded OR ID differs)
      const auditId = this.$route.params.auditId;
      if (auditId && (!this.audit || this.audit._id !== auditId)) {
        await this.$store.dispatch("firmAudit/findById", auditId);
      }
    } catch (error) {
      this.$handleHttpErrorByStatusCode(error);
    } finally {
      this.isInitializing = false;
    }
  },
  computed: {
    pagination() {
      return this.$store.getters["firmPolicy/detailPagination"];
    },
    isDetailLoading() {
      return this.$store.getters["firmPolicy/isDetailLoading"];
    },
    firm() {
      return this.$store.getters["organizationFirm/selectedEntity"];
    },
    currentProduct() {
      return this.$store.getters["product/currentProduct"] || "FWSED";
    },
    audit() {
      return this.$store.getters["firmAudit/selectedEntity"];
    },
    firmStatus() {
      return getFirmStatus(this.firm, this.currentProduct);
    },
    docs() {
      return this.pagination?.docs ?? [];
    },
    hasDocs() {
      return this.docs.length > 0;
    },

    completedPolicyCount() {
      return this?.pagination?.completedCount || "-";
    },
    activeStatus() {
      return this.statusTabs[this.activeTabIndex]?.key ?? "IN_PROGRESS";
    },
    policyBlocks() {
      return this.docs.map((policy, index) => {
        const questions = this.getQuestions(policy);
        return {
          policy,
          questions,
          questionCount: questions.length,
          key: policy._id || policy.id || index,
        };
      });
    },
  },
  watch: {
    nameFilter(n, o) {
      if (n) {
        this.request.query = { ...this.request.query, name: { value: n } };
      } else {
        this.request.query["name"] = null;
        delete this.request.query["name"];
      }
    },
    questionCategoryFilter(n, o) {
      if (n !== null && n !== undefined) {
        this.request.query = {
          ...this.request.query,
          questionCategory: { value: n, isObject: true },
        };
      } else {
        this.request.query["questionCategory"] = null;
        delete this.request.query.questionCategory;
      }
    },
    firmId: {
      immediate: true,
      async handler() {
        if (!this.firmId) {
          try {
            await this.fetchPolicies();
          } finally {
          }
          return;
        }

        this.request.options.page = 1;
        this.setStatusFilter(this.activeStatus);
        try {
          await this.fetchPolicies();
        } finally {
        }
      },
    },
    activeTabIndex: {
      async handler(newIndex, oldIndex) {
        if (newIndex === oldIndex || !this.statusTabs[newIndex]) {
          return;
        }
        this.request.options.page = 1;
        this.setStatusFilter(this.activeStatus);
        try {
          await this.fetchPolicies();
        } finally {
        }
      },
    },
    request: {
      deep: true,
      immediate: true,
      async handler(newVal, oldVal) {
        try {
          this.debounce(() => this.fetchPolicies());
        } finally {
        }
      },
    },
  },
  methods: {
    goBack() {
      if (!this.firm) {
        return;
      }
      this.$router.push(`/fwsed/firm-management/${this.firm._id}/audit`);
    },
    async prepareReport(firm) {
      try {
        const { data: file } = await this.$store.dispatch("firmPolicy/prepareFindeksByFirm", { firm, audit: this.audit });
        this.download(file);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async download(item) {
      this.$store.commit("fileManager/addDownloadedFile", { file: item });
    },

    isTabActive(key) {
      return this.activeStatus === key;
    },
    setStatusFilter(status) {
      this.request.query.status = { value: status, isObject: true };
    },
    async fetchPolicies() {
      if (!this.firmId) {
        this.$handleHttpErrorByStatusCode({ message: this.$t("error.firm-not-found") }, 404);
      }
      const pagination = {
        options: { ...this.request.options },
        query: JSON.parse(JSON.stringify(this.request.query)),
      };
      
      await this.$store.dispatch("firmPolicy/fetchFirmControlPolicy", { firmId: this.firmId, auditId: this.audit?._id, pagination });
    },
    refresh() {
      this.fetchPolicies();
    },

    getQuestions(policy) {
      const questions = policy?.questions;
      return Array.isArray(questions) ? questions : [];
    },

    formatPolicyStatus(status) {
      if (!status) {
        return "-";
      }
      const normalized = String(status).toUpperCase();
      const map = {
        WAITING_FOR_ASSESSMENT: this.$t("policy.status.waiting-for-assessment"),
        IN_PROGRESS: this.$t("policy.status.in-progress"),
        WAITING_FOR_PERIOD: this.$t("policy.status.waiting-for-period"),
        COMPLETED: this.$t("policy.status.completed"),
        NOT_APPLICABLE: this.$t("policy.status.not-applicable"),
      };
      return map[normalized] ?? status;
    },
    formatQuestionLabel(question) {
      if (!question) {
        return "-";
      }
      const parts = [];
      if (question.questionSet?.name) {
        parts.push(this.$options.filters.translate(question.questionSet.name));
      }
      if (question.name) {
        parts.push(this.$options.filters.translate(question.name));
      } else if (question.question?.name) {
        parts.push(this.$options.filters.translate(question.question.name));
      } else if (question.questionText) {
        parts.push(this.$options.filters.translate(question.questionText));
      } else if (question.code) {
        parts.push(question.code);
      } else if (question.question?.code) {
        parts.push(question.question.code);
      }
      if (parts.length === 0 && question.questionNo) {
        parts.push(`#${question.questionNo}`);
      }
      return parts.length ? parts.join(" - ") : "-";
    },
    formatQuestionStatus(status) {
      if (!status) {
        return "-";
      }
      const normalized = String(status).toUpperCase();
      const map = {
        NONE: this.$t("firm.audit.review.status.none"),
        PENDING: this.$t("firm.audit.review.status.pending"),
        APPROVED: this.$t("firm.audit.review.status.approved"),
        REVISION_REQUIRED: this.$t("firm.audit.review.status.revision"),
      };

      return map[normalized] ?? status;
    },
  },
};
</script>

<style scoped>
.policy-card__icon {
  border-radius: 50%;
  /* background: #f4f6fb; */

  place-items: center;
  color: #6a6f85;
  font-size: 1.1rem;
}
.policy-table {
  width: 100%;
  border-collapse: collapse;
}

.policy-table thead th {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  padding: 0.75rem 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.policy-table__row {
  background-color: #ffffff;
}
.policy-table__cell {
  text-align: left;
}
.policy-table__row--first .policy-table__cell,
.policy-table__row--single .policy-table__cell {
  border-top: 1px solid #e2e8f0;
}

.policy-table__cell--status {
  text-align: center;
  vertical-align: middle;
  min-width: 160px;
  white-space: nowrap;
}
th {
  text-align: center;
  vertical-align: middle;
}
td {
  text-align: center;
  vertical-align: middle;
}
.policy-table__cell--question,
.policy-table__cell--question-status {
  min-width: 220px;
}

.policy-table__cell--question {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.policy-table__question-bullet {
  color: #2563eb;
  font-size: 1rem;
  line-height: 1.1;
}

.policy-table__question-text {
  flex: 1;
  color: #1e293b;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.policy-table__cell--question-status {
  vertical-align: middle;
  white-space: nowrap;
}

.policy-table__question-status {
  font-size: 0.75rem;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
}

.policy-table__row:last-child .policy-table__cell {
  border-bottom: none;
}
</style>
